import React from 'react';
import { Text as RNText, StyleSheet, TextProps as RNTextProps } from 'react-native';
import {
  colors,
  MAX_FONT_SCALE,
  textVariants,
  TextVariant,
} from '../../theme';

/** Default ink for each variant, so most usages need no `color` prop. */
const defaultColor: Record<TextVariant, string> = {
  display: colors.ink,
  displaySm: colors.ink,
  titleLg: colors.ink,
  title: colors.ink,
  subtitle: colors.ink,
  body: colors.inkBody,
  bodySm: colors.inkMuted,
  label: colors.ink,
  labelBold: colors.ink,
  labelSm: colors.inkBody,
  caption: colors.inkMuted,
  eyebrow: colors.inkMuted,
};

export type TextProps = RNTextProps & {
  /** Type scale entry. See `src/theme/typography.ts`. */
  variant?: TextVariant;
  /** Overrides the variant's default ink. */
  color?: string;
  align?: 'left' | 'center' | 'right';
};

/**
 * The only text component the app should use. Wrapping RN's `Text` keeps
 * font family, size, line height and ink in the theme rather than scattered
 * across screen files.
 *
 * ---------------------------------------------------------------------------
 * WHY THE OS FONT SETTING IS CAPPED HERE
 * ---------------------------------------------------------------------------
 * Text scaling runs to 3.5× on iOS and 2× on Android, and it multiplies every
 * size in the ramp without touching a single one of the fixed heights around
 * them - 44 and 52pt buttons, a 48pt chat bar, two-up rows whose halves must
 * stay on one line. At 2× the labels are simply cut off inside controls that
 * did not grow, which is worse for the person who turned the setting on than
 * type that grew a little less than they asked for.
 *
 * `MAX_FONT_SCALE` (1.3) is as far as these layouts stretch intact, and it
 * still honours the first several steps of the setting. Setting it once here
 * covers every string in the app; a screen that has room for more can pass its
 * own `maxFontSizeMultiplier` and override it.
 */
function Text({ variant = 'body', color, align, style, ...rest }: TextProps) {
  return (
    <RNText
      maxFontSizeMultiplier={MAX_FONT_SCALE}
      {...rest}
      style={[
        styles[variant],
        { color: color ?? defaultColor[variant] },
        align ? { textAlign: align } : null,
        style,
      ]}
    />
  );
}

const styles = StyleSheet.create(textVariants);

export default Text;
